import {
  AdminRoleFunction,
  UserGroup,
  UserFunction,
  EmployeeRoles,
  EmployeeRole,
  UserAdmin
} from 'common/types/admin.types'
import { useEffect, useState } from 'react'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { useAnalytics, AnalyticUserType } from 'hooks/use-analytics/useAnalytics'
import { usePartnerApi } from 'hooks/use-partner-api/usePartnerApi'
import { useParams } from 'react-router-dom'
import { useUserApi } from 'hooks/use-user-api/useUserApi'
import { useNotification } from 'hooks/use-notification/useNotification'
import { BackendUser, LandingPageUser, UserGroupUsers } from 'common/types'

export const usePartnerManagement = () => {
  const [page, setPage] = useState(1)
  const [userGroup, setUserGroup] = useState<UserGroup[]>([])
  const [userFunctions, setUserFunctions] = useState<UserFunction[]>([])
  const [bptnUsers, setBptnUsers] = useState<EmployeeRole[]>([])
  const [currentAdminUsers, setCurrentAdminUsers] = useState<UserGroupUsers>({data:[]})
  const [partnerAdminUsers, setPartnerAdminUsers] = useState<UserAdmin[]>([])
  const [userExistError, setUserExistError] = useState(false)
  const [roleId, setRoleId] = useState<string>()
  const [groupFunctions, setGroupFunctions] = useState([])
  const { backendUser: backendUser, backendPartner: backendPartner, backendUserFunctions } = useAuth()
  const { logEventAnalytics } = useAnalytics()
  const { notify } = useNotification()
  const {
    createPartnerGroups,
    fetchPartnerFunctions,
    deletePartnerGroups,
    fetchPartnerFunctionAndRoleByRole,
    fetchPartnerFunctionsByUser,
    fetchPartnerGroupsByPartnerId,
    fetchPartnerRoleByPartnerId,
    fetchPartner,
    createPartnerAdminRoles,
    createPartnerAdmin,
    updatePartnerGroup,
    disablePartnerUserGroup,
    deleteAssignedPartnersByRole,
    createBulkPartnerFunction,
    fetchPartnerUserAndRolesByRole,
    fetchPartnerUserAndRolesByUserId,
    deletePartnerJob,
    closePartnerJob,
    fetchPartnerAdminUsersByPartnerId,
    fetchAdminUserGroupsByRoleId
  } = usePartnerApi()
  //Assign Admin State
  const { groupId } = useParams<{ groupId: string }>()
  const { getUserWithEmail, fetchUser, updateUser,sendAdminInvitationToNonObsidiMember } = useUserApi()
  const [value, setValue] = useState<string | null>()
  const [inputValue, setInputValue] = useState<string>()
  const [userId, setUserId] = useState<string>()
  const [username, setUsername] = useState<string>()
  const [userDoesntExist, setUserDoesntExist] = useState<boolean>(false)
  const [existingAdminUserError, setExistingAdminUserError] = useState<boolean>(false)
  const [isLoading, setLoaderStatus] = useState(true)
  const [isInitialLoading, setInitialLoaderStatus] = useState(true)
  const [isFetchingMore, setFetchMoreStatus] = useState(false)
  const {handleTracker} = useAnalytics()
  const [disclaimerPopUp, setDisclaimerPopUp] = useState(false)

  const getPartnerGroups = async () => {
    if (backendUser?.partnerId) {
      const role = await fetchPartnerGroupsByPartnerId(backendUser?.partnerId)
      setUserGroup(role)
    }
  }

  const getUserFunctions = async () => {
    setLoaderStatus(true)
    const userFunction = await fetchPartnerFunctions(page, 10)
    setUserFunctions(userFunction)
    setLoaderStatus(false)
  }

  const getGroupFunctions = async () => {
    if (roleId) {
       setLoaderStatus(true)
      const permissions = await fetchPartnerFunctionAndRoleByRole(roleId)
      setGroupFunctions(permissions.map(x => x?.functionId))
      setLoaderStatus(false)
      return
    }
    setGroupFunctions([])
  }

  const createUserGroupWithPermissions = async (role: UserGroup, permissions: UserFunction[]) => {
    let groupId = roleId
    if (roleId) {
      const response = await updatePartnerGroup(backendUser?.id, role, roleId)
      const groupIndex = userGroup?.findIndex(group => group.id === response?.id)
      const updatedUserGroups = [...userGroup]
      updatedUserGroups[groupIndex] = response
      setUserGroup(updatedUserGroups)
    } else {
      const response = await createPartnerGroups(backendUser?.id, role)
      const newGroup: UserGroup = {
        id: response?.id,
        name: response?.name,
        description: response?.description,
        createdBy: response?.createdBy,
        createdAt: response?.createdAt
      }
      groupId = response?.id
      setUserGroup([newGroup, ...userGroup])
      const partnerLog = {}
      const userLog = {}
      userLog['User ID'] = backendUser?.id
      if (backendUser?.partnerId) {
        partnerLog['Partner ID'] = backendPartner?.id
        partnerLog['Company Name'] = backendPartner?.name
        partnerLog['Account Type'] = 'Partner'
      } else {
        userLog['Account Type'] = backendUser?.role
      }
      logEventAnalytics(
        backendUser?.partnerId ? 'Partner' : backendUser?.role,
        'create user group',
        'mixpanel',
        'create user group clicked',
        {
          'User Group ID': response?.id,
          Name: response?.name,
          ...partnerLog,
          ...userLog
        },
        backendUser?.id
      )
    }
    if (permissions?.length) {
      const roleFunc = permissions.map((x: UserFunction) => ({
        functionId: x?.id,
        roleId: groupId,
        partnerId: backendUser?.partnerId,
        functionName: x?.name
      }))
      await createBulkPartnerFunction(backendUser?.id, groupId, roleFunc)
    }
  }

  const getAdminUsersByRole = async (roleId: string) => {
    return await fetchPartnerUserAndRolesByRole(roleId)
  }
  //Getting all partner admin users by partnerId
  const getAdminUsersByPartnerId = async () => {
    if (backendUser?.partnerId) {
      const data = await fetchPartnerAdminUsersByPartnerId(backendUser?.partnerId)
      setPartnerAdminUsers(data)
    }
  }

  const getCurrentAdminUser = async (
    userGroupRoleId?: string,
    withMetaData: boolean = true,
    page: number = 1,
    withPagination:boolean=false,
    pageSize: number = 10
  ) => {


    const groupRoleId = userGroupRoleId || roleId


    if (groupRoleId) {
     page === 1 && setInitialLoaderStatus(true)
      const response = await fetchPartnerUserAndRolesByRole(groupRoleId, page, withMetaData, pageSize)
      setInitialLoaderStatus(false)
  
      if (withPagination) {
        const users = currentAdminUsers?.data || []
        setCurrentAdminUsers({
          ...response,
          data: [...users, ...response?.data]
        })
      } else {
        
        setCurrentAdminUsers({
          ...response,
         data: response?.data
        })
      }
  
    }
  }

  const assignUserToGroup = async (username: string, userId: string) => {
    const existingAdminUser: UserAdmin = await fetchPartnerUserAndRolesByUserId(userId)
    if (existingAdminUser) {
      setExistingAdminUserError(true)
      return
    }
    const response = await createPartnerAdmin(roleId, userId)
    setCurrentAdminUsers({...currentAdminUsers, data:[{ ...response, firstname: username }, ...currentAdminUsers?.data]})
    return response
  }

  const assignGroupToUser = async (roleId: string, userId: string) => {
    const response = await createPartnerAdmin(roleId, userId)
    if (response) {
      const index = bptnUsers?.findIndex(bu => bu.userId === userId)
      const updatedUserInfo = [...bptnUsers]
      updatedUserInfo[index]!.numOfRoles = (Number(updatedUserInfo[index]!.numOfRoles) + 1).toString()
      updatedUserInfo[index]!.roles += `,${response?.roleName}`
      setBptnUsers(updatedUserInfo)
    }
  }

  const isDuplicateUserGroup = (name: string) => {
    return userGroup?.some(user => user?.name?.toLowerCase() === name?.toLowerCase())
  }

  //Assign Admin Functions
  const handleAddChange = (newInputValue: string) => {
    const index = currentAdminUsers?.data?.findIndex(u => u.username === newInputValue)
   
    
   
    const existAlready = !newInputValue || index === -1
    setUserExistError(!existAlready)
  }
  const handleAddClick = async () => {
    const user: LandingPageUser = await getUserWithEmail(value?.toLowerCase())
    if (user?.userExists) {
      if (!userDoesntExist && !userExistError) {
        const response = await assignUserToGroup(value?.toLowerCase(), user?.userId)
        if (response) {
          const userToUpdate: BackendUser = await fetchUser(user?.userId)
          const userGroupDetails = await fetchAdminUserGroupsByRoleId(response?.roleId)
          await updateUser({ ...userToUpdate, partnerId: backendPartner?.id })
          notify('success', 'User added successfully')
          handleAddClose()
          const logData = {
            ['User ID']: backendUser?.id,
            ['Account Type']: backendUser?.partnerId ? 'Partner' : backendUser?.role,
            ['User Group ID']: response?.id,
            ['Name']: userGroupDetails?.name,
            ['Assignee ID']: response?.userId,
            ['Assignee Email']: response?.username,
            ['Partner ID']: backendPartner?.id,
            ['Company Name']: backendPartner?.name
          }
          await handleTracker(
            AnalyticUserType.ALL,
            'Assign User to User group',
            logData,
            'mixpanel',
            backendPartner,
            backendUser
          )
          setUserDoesntExist(!user?.userExists)
          return response
        }
      }
    } else {
      setUserDoesntExist(!user?.userExists)
      setDisclaimerPopUp(!user?.userExists)
    }
  }

  const handleNonObsidiMemberAdminInvite = async () => {
    if (userDoesntExist) {
      const response = await sendAdminInvitationToNonObsidiMember(value?.toLowerCase(), backendUser?.id, backendPartner?.name)
      return response
    }
  }

  const handleAddClose = () => {
    setValue('')
    setInputValue('')
    setUsername('')
    setUserId('')
    setRoleId(undefined)
    if (userDoesntExist) setUserDoesntExist(false)
    if (userExistError) setUserExistError(false)
    if (existingAdminUserError) setExistingAdminUserError(false)
  }

  const compareArrayStrings = (array1: string[], array2: string[]): boolean => {
    return array1?.every((value, index) => value === array2[index])
  }

  const fetchMore = async (e:any,userGroupRoleId:string,dataLength:number) => {
    const bottom = e.target.scrollTop < 3

    const totalCount = `${currentAdminUsers?.totalCount} sd`


    if (bottom && !isLoading && !isFetchingMore && parseInt(totalCount || '0') > dataLength) {
      setFetchMoreStatus(true)

      await getCurrentAdminUser(userGroupRoleId, true, parseInt(currentAdminUsers?.page) + 1, true)
      setFetchMoreStatus(false)
    }
  }


  useEffect(() => {
    getPartnerGroups()
    getCurrentAdminUser()
    getGroupFunctions()
  }, [roleId])

  useEffect(() => {
    getUserFunctions()
    if (groupId) setRoleId(groupId)
  }, [])

  return {
    roleId,
    userGroup,
    setRoleId,
    getPartnerGroups,
    setUserGroup,
    createUserGroupWithPermissions,
    userFunctions,
    deletePartnerGroups,
    disablePartnerUserGroup,
    getAdminUsersByRole,
    bptnUsers,
    backendUser,
    backendPartner,
    currentAdminUsers,
    setCurrentAdminUsers,
    assignUserToGroup,
    deleteAssignedPartnersByRole,
    assignGroupToUser,
    userExistError,
    setUserExistError,
    groupFunctions,
    setGroupFunctions,
    isDuplicateUserGroup,
    handleAddChange,
    handleAddClick,
    handleAddClose,
    value,
    setValue,
    userDoesntExist,
    setUserDoesntExist,
    existingAdminUserError,
    setExistingAdminUserError,
    fetchUser,
    updateUser,
    deletePartnerJob,
    closePartnerJob,
    getAdminUsersByPartnerId,
    partnerAdminUsers,
    setPartnerAdminUsers,
    compareArrayStrings,
    getCurrentAdminUser,
    isLoading,
    setLoaderStatus,
    fetchMore,
    isFetchingMore,
    isInitialLoading,
    disclaimerPopUp,
    setDisclaimerPopUp,
    handleNonObsidiMemberAdminInvite
  } as const
}
